<script lang="ts" setup>
import type { PageHeaderMenuItem } from '~/components/page/header/menu/PageHeaderMenu.vue'
import { Link } from '@unhead/schema'
import type { SrcSet } from '~/composables/useSrcSet'
import type { ApiBlogCategory } from '~~/server/api/blog/category'

const route = useRoute()
const { $t } = useNuxtApp()

const { data: page, redirect } = await useFetchWithRedirect<ApiBlogCategory>(
    '/api/blog/category',
    { route },
    {
        params: {
            id: route.params.id || 'magazin',
            s: route.query.s || 1,
            per_page: 13,
        },
    }
)

if (redirect && !redirect?.error) {
    navigateTo(redirect.data.url, redirect.data.options)
}

const featuredPostsCount = 4

const featuredPost = computed<BlogPost | undefined>(
    (): BlogPost | undefined => page.value?.data.posts.data[0]
)
const featuredPosts = computed<BlogPost[]>(
    (): BlogPost[] =>
        page.value?.data.posts.data.slice(1, featuredPostsCount) || []
)
const othersPosts = computed<BlogPost[]>(
    (): BlogPost[] =>
        page.value?.data.posts?.data.slice(featuredPostsCount) || []
)

const sections = computed<PageHeaderMenuItem[]>((): PageHeaderMenuItem[] => {
    if (!page.value) return []

    return page.value.data.categories.map((category) => {
        if (category.meta_category) {
            return {
                hash: `/${category.slug}`, 
                title: category.title,
            } as PageHeaderMenuItem
        }
        return {
            hash: `/magazin/kategoria/${category.slug}`, 
            title: category.title,
        } as PageHeaderMenuItem
    })
})

const linkType = computed<BlogPostType>(() =>
    useBlogPostLinkType(String(route.params.id))
)
const config = useRuntimeConfig()
const breadcrumbs = computed<BreadcrumbsLink[]>(() => {
    if (!route.params.id) return []

    return [
        {
            name: $t('Magazín'),
            slug: useNamedRoutes({ name: 'magazine' }),
        },
    ]
})
const headLinks = computed<Link[]>((): Link[] => {
    if (Number(route.query.s) !== 1) return []
    return [
        {
            rel: 'canonical',
            href: `${config.public.publicPath}${useNamedRoutes({
                name: 'magazine',
            })}`,
        },
    ]
})
const headerImageSrc = computed((): SrcSet | undefined =>
    useSrcSet(page.value?.data.magazineHeaderImage?.path as ThumborThumbnail)
)

useSchemaOrg([
    defineArticle({
        name: page.value?.data.headline.headline,
        articleSection: sections.value.map(
            (section: PageHeaderMenuItem): string => section.title
        ),
    }),
])

useHead({
    link: headLinks.value,
})

if (route.params.id) {
    useCanonicalHead({ routeName: useNamedRoutes({ name: 'magazine' }) })
}

// Page meta
usePageMeta({
    meta: page.value?.data.meta,
    title: page.value?.data.title,
    og_type: 'article',
})
</script>
<template>
  <div v-if="page">
    <ContainerFluid>
      <Breadcrumbs
        :current="page?.data.title || $t('Magazín')"
        :links="breadcrumbs"
      />
    </ContainerFluid>
    <PageHeader
      :headline="page.data.headline"
      :title="page.data.title"
      class="mb-12"
      :sections="sections"
      :data="page"
    >
      <template
        v-if="!route.params.id && headerImageSrc"
        #image
      >
        <h1 class="visually-hidden">
          {{ $t("Magazín") }}
        </h1>
        <img
          :src="headerImageSrc?.src"
          :srcset="headerImageSrc?.srcSet"
          alt=""
          width="316"
          height="130"
          class="d-block mx-auto mb-4"
        >
      </template>
    </PageHeader>

    <ContainerFluid>
      <template v-if="page.data.posts.data.length > 0">
        <ArticleBoxFeatured
          v-if="featuredPost"
          :data="featuredPost"
          :link-type="linkType"
          class="mb-12 mb-md-6"
        />
        <div
          v-if="featuredPosts?.length > 0"
          class="row"
        >
          <div
            v-for="(article, index) in featuredPosts"
            :key="`mfp-${index}`"
            class="col-12 col-md-6 col-lg-4 mb-11"
          >
            <Tracker :data="{ position: 'Article Box' }">
              <ArticleBox
                :link-type="linkType"
                :data="article"
                :type="'secondary'"
                heading-level="h2"
              />
            </Tracker>
          </div>
        </div>
        <WidgetArticlesMostRead
          v-if="page.data.most_popular_posts?.length > 0"
          class="mb-10 mb-md-20"
          heading-level="h2"
          :data="page.data.most_popular_posts"
        />
        <div
          v-if="othersPosts.length > 0"
          class="row"
        >
          <div
            v-for="(article, index) in othersPosts"
            :key="`mip-${index}`"
            class="col-12 col-md-6 col-lg-4 mb-11"
          >
            <Tracker :data="{ position: 'Article Box' }">
              <LazyArticleBox
                :link-type="linkType"
                :data="article"
                :type="'secondary'"
                :alt-switch="false"
              />
            </Tracker>
          </div>
        </div>
        <Pagination
          :pagination="page.data.posts.pagination"
          :link="linkType"
          class="mb-12 mb-md-20"
        />
        <div class="pt-12 pt-md-20 border-top">
          <LazyWidgetHelpdeskExt />
        </div>
      </template>
      <Typo v-else>
        <p class="text-center mb-12 mb-md-20 fs-big">
          {{ $t("V kategórie nie sú momentálne žiadne články.") }}
        </p>
      </Typo>
    </ContainerFluid>
  </div>
</template>
